
import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    useMultiStyleConfig,
    IconButton,
    Button,
    Img,
    Link as ChakraLink,
    Flex,
    Spinner,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    useDisclosure,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl, defineMessage} from 'react-intl'

/* Components */
import Link from '@salesforce/retail-react-app/app/components/link'
import Field from '../../field'
import WebDrawer from '../../web-drawer'

/* Hooks */
import {useForm} from 'react-hook-form'
import {useNewsletter} from '../../../integrations/newsletter/hooks/use-newsletter'
import {useSmsSubscription} from '../../../integrations/newsletter/hooks/use-sms-subscription'
import {useBrand} from '../../../contexts/brand-context'
import {useLegalPagePaths} from '../../../hooks/use-legal-page-paths'
import {useGlobalNewsletterSubscription} from '../../../contexts/global-elements-context'
import {useGlobalSmsSubscription} from '../../../contexts/global-elements-context'

/* Icons */
import {RightArrowWhite, ConfirmationIcon, CustomClose} from '../../icons'
import {parseShortcodes} from '../../optimizely/gdi/utils/gdi-shortcode-parser'
import {useShortcodeLinkUrl} from '../../optimizely/gdi/utils/use-shortcode-link-url'
import {convertCmsStyles} from '../utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

const newsletterServerErrorGarage = defineMessage({
    defaultMessage: 'Something went wrong. Please try again.',
    id: 'newsletter.error.server.garage'
})
const newsletterServerErrorDynamite = defineMessage({
    defaultMessage: 'Something went wrong. Please try again.',
    id: 'newsletter.error.server.dynamite'
})
const newsletterEmailInvalidGarage = defineMessage({
    defaultMessage: 'Please enter a valid email address',
    id: 'newsletter.field.error.email.garage'
})
const newsletterEmailInvalidDynamite = defineMessage({
    defaultMessage: 'Please enter a valid email address',
    id: 'newsletter.field.error.email.dynamite'
})
const newsletterPhonePatternGarage = defineMessage({
    defaultMessage: 'Please enter a valid phone number (e.g. 888-888-8888)',
    id: 'newsletter_phone.error.pattern.garage'
})
const newsletterPhonePatternDynamite = defineMessage({
    defaultMessage: 'Please enter a valid phone number (e.g. 888-888-8888)',
    id: 'newsletter_phone.error.pattern.dynamite'
})
const newsletterSmsThanksGarage = defineMessage({
    defaultMessage: 'Thanks for subscribing',
    id: 'newsletter_confirmation.message.sms.garage'
})
const newsletterSmsThanksDynamite = defineMessage({
    defaultMessage: 'Thanks for subscribing',
    id: 'newsletter_confirmation.message.sms.dynamite'
})

const COMPONENT_CONSTANTS = {
    newsletterType: 'Footer',
    newsletterSource: 'WebSite'
}

const NewsletterSignUp = ({...otherProps}) => {
    const styles = useMultiStyleConfig('Newsletter')
    const intl = useIntl()
    const {isGarage} = useBrand()
    const {privacyPolicyPath, termsAndConditionsPath} = useLegalPagePaths()
    const brandName = isGarage ? 'Garage' : 'Dynamite'
    const {data: newsletterSubscription} = useGlobalNewsletterSubscription()
    const {data: smsSubscription} = useGlobalSmsSubscription()
    const shortcodeBuildUrl = useShortcodeLinkUrl()
    const shortcodeOptions = {brandName, buildUrl: shortcodeBuildUrl}
    const smsImageUrl = smsSubscription?.imageUrl
    const cmsSmsTitleText = smsSubscription?.title?.textForWeb
        ? parseShortcodes(smsSubscription.title.textForWeb, shortcodeOptions)
        : null
    const cmsSmsTitleStyles = convertCmsStyles(smsSubscription?.title?.stylesPerViewType)
    const cmsSmsDescText = smsSubscription?.description?.textForWeb
        ? parseShortcodes(smsSubscription.description.textForWeb, shortcodeOptions)
        : null
    const cmsSmsDescStyles = convertCmsStyles(smsSubscription?.description?.stylesPerViewType)
    const cmsSmsPrivacyText = smsSubscription?.privacyText?.textForWeb
        ? parseShortcodes(smsSubscription.privacyText.textForWeb, shortcodeOptions)
        : null
    const cmsSmsPrivacyStyles = convertCmsStyles(smsSubscription?.privacyText?.stylesPerViewType)
    const cmsTitleText = newsletterSubscription?.title?.textForWeb
        ? parseShortcodes(newsletterSubscription.title.textForWeb, shortcodeOptions)
        : null
    const cmsTitleStyles = convertCmsStyles(newsletterSubscription?.title?.stylesPerViewType)
    const cmsPrivacyText = newsletterSubscription?.privacyText?.textForWeb
        ? parseShortcodes(newsletterSubscription.privacyText.textForWeb, shortcodeOptions)
        : null
    const cmsPrivacyStyles = convertCmsStyles(newsletterSubscription?.privacyText?.stylesPerViewType)
    const form = useForm({mode: 'onSubmit'})
    const {setError, control, formState: {errors}} = form
    const {isOpen: isSmsModalOpen, onOpen: openSmsModal, onClose: _closeSmsModal} = useDisclosure()
    const closeSmsModal = () => {
        setSmsSubmittedWithSuccess(false)
        smsForm.reset()
        _closeSmsModal()
    }
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const smsForm = useForm({mode: 'onSubmit'})
    const {control: smsControl, formState: {errors: smsErrors}, handleSubmit: handleSmsSubmit} = smsForm
    const [isSmsSubmitting, setIsSmsSubmitting] = useState(false)
    const [smsSubmittedWithSuccess, setSmsSubmittedWithSuccess] = useState(false)
    const [smsData, setSmsData] = useState({
        email: null,
        phone: null,
        type: null,
        source: null
    })
    const [smsPendingSubmit, setSmsPendingSubmit] = useState(false)
    const {submitSmsSubscription} = useSmsSubscription({...smsData})
    const [newsletterData, setNewsletterData] = useState({
        email: null,
        type: null,
        source: null
    })
    const [pendingSubmit, setPendingSubmit] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submittedWithSuccess, setSubmittedWithSuccess] = useState(false)
    const {submitNewsletter} = useNewsletter({...newsletterData})

    /* submitNewsletter is bound at render time to the current newsletterData.
     * We must wait for the state update (and re-render) before calling it,
     * otherwise it fires with stale null values. */
    useEffect(() => {
        if (!pendingSubmit || !newsletterData.email) return

        const doSubmit = async () => {
            setIsSubmitting(true)
            try {
                const response = await submitNewsletter()
                if (response?.success) {
                    setError('formError', {type: '', message: ''})
                    setSubmittedWithSuccess(true)
                    openSmsModal()
                } else {
                    const message = response?.emailInvalid
                        ? intl.formatMessage(
                              isGarage
                                  ? newsletterEmailInvalidGarage
                                  : newsletterEmailInvalidDynamite
                          )
                        : intl.formatMessage(
                              isGarage ? newsletterServerErrorGarage : newsletterServerErrorDynamite
                          )
                    setError('formError', {
                        type: 'manual',
                        message
                    })
                    setSubmittedWithSuccess(false)
                }
            } catch {
                setError('formError', {
                    type: 'manual',
                    message: intl.formatMessage(
                        isGarage ? newsletterServerErrorGarage : newsletterServerErrorDynamite
                    )
                })
                setSubmittedWithSuccess(false)
            }
            setIsSubmitting(false)
            setPendingSubmit(false)
        }

        doSubmit()
    }, [pendingSubmit, newsletterData.email]) // eslint-disable-line react-hooks/exhaustive-deps

    /* submitSmsSubscription is bound at render time — same deferred pattern as email. */
    useEffect(() => {
        if (!smsPendingSubmit || !smsData.phone || !smsData.email) return

        const doSmsSubmit = async () => {
            setIsSmsSubmitting(true)
            try {
                const response = await submitSmsSubscription()
                if (response?.success) {
                    setSmsSubmittedWithSuccess(true)
                } else {
                    const msg = response?.phoneInvalid
                        ? intl.formatMessage(
                              isGarage
                                  ? newsletterPhonePatternGarage
                                  : newsletterPhonePatternDynamite
                          )
                        : intl.formatMessage(
                              isGarage ? newsletterServerErrorGarage : newsletterServerErrorDynamite
                          )
                    smsForm.setError('newsletterPhone', {type: 'manual', message: msg})
                }
            } catch {
                smsForm.setError('newsletterPhone', {
                    type: 'manual',
                    message: intl.formatMessage(
                        isGarage ? newsletterServerErrorGarage : newsletterServerErrorDynamite
                    )
                })
            }
            setIsSmsSubmitting(false)
            setSmsPendingSubmit(false)
        }

        doSmsSubmit()
    }, [smsPendingSubmit, smsData.phone, smsData.email]) // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = (data) => {
        setNewsletterData({
            email: data?.newsletterEmail,
            type: COMPONENT_CONSTANTS.newsletterType,
            source: COMPONENT_CONSTANTS.newsletterSource
        })
        setPendingSubmit(true)
    }

    const onSmsSubmit = (data) => {
        // Format: 888-888-8888 → (888) 888-8888
        const raw = (data?.newsletterPhone || '').replace(/\D/g, '')
        const formatted = raw.length === 10
            ? `(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6)}`
            : data?.newsletterPhone
        setSmsData({
            email: newsletterData.email,
            phone: formatted,
            type: COMPONENT_CONSTANTS.newsletterType,
            source: COMPONENT_CONSTANTS.newsletterSource
        })
        setSmsPendingSubmit(true)
    }

    const smsFormContent = (
        <>
            <Flex {...styles.smsHeader}>
                <Box {...styles.smsTitle}>
                    {cmsSmsTitleText ? (
                        <Box
                            sx={{
                                '& p': {textStyle: 'webH3'},
                                '& h1': {textStyle: 'webH1'},
                                '& h2': {textStyle: 'webH2'},
                                '& h3': {textStyle: 'webH3'},
                                '& h4, & h5, & h6': {textStyle: 'webH4'}
                            }}
                            {...cmsSmsTitleStyles}
                            dangerouslySetInnerHTML={{__html: cmsSmsTitleText}}
                        />
                    ) : (
                        <Text textStyle="webH3">
                            {isGarage ? (
                                <FormattedMessage
                                    defaultMessage="Want texts too?"
                                    id="newsletter_sms_modal.title.garage"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Want texts too?"
                                    id="newsletter_sms_modal.title.dynamite"
                                />
                            )}
                        </Text>
                    )}
                </Box>
                <IconButton
                    variant="unstyled"
                    aria-label={intl.formatMessage({defaultMessage: 'Close', id: 'sms_modal.close'})}
                    icon={<CustomClose />}
                    onClick={closeSmsModal}
                    {...styles.smsCloseButton}
                    data-testid="sms-modal-close-button"
                />
            </Flex>
            {cmsSmsDescText ? (
                <Box
                    sx={{
                        '& p': {textStyle: 'bodySmRegular'},
                        '& a': {textDecoration: 'underline'},
                        '& a:hover': {textDecoration: 'underline'}
                    }}
                    {...cmsSmsDescStyles}
                    {...styles.smsSubtitle}
                    dangerouslySetInnerHTML={{__html: cmsSmsDescText}}
                />
            ) : (
                <Text textStyle="bodySmRegular" {...styles.smsSubtitle}>
                    {isGarage ? (
                        <FormattedMessage
                            defaultMessage="Add it for SMS updates on drops and exclusive offers — or skip and stick to email."
                            id="newsletter_sms_modal.subtitle.garage"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Add it for SMS updates on drops and exclusive offers — or skip and stick to email."
                            id="newsletter_sms_modal.subtitle.dynamite"
                        />
                    )}
                </Text>
            )}
            <form onSubmit={handleSmsSubmit(onSmsSubmit)} noValidate>
                <Field
                    key="phone"
                    name="newsletterPhone"
                    type="tel"
                    variant="webFloatingField"
                    control={smsControl}
                    defaultValue=""
                    label={intl.formatMessage({
                        defaultMessage: 'Phone Number (optional)',
                        id: 'newsletter_phone.label_optional'
                    })}
                    rules={{
                        pattern: {
                            value: /^\d{3}-\d{3}-\d{4}$/,
                            message: intl.formatMessage(
                                isGarage ? newsletterPhonePatternGarage : newsletterPhonePatternDynamite
                            )
                        }
                    }}
                    error={smsErrors?.newsletterPhone}
                    formControlProps={{isDisabled: isSmsSubmitting}}
                    webHelperText={
                        smsSubmittedWithSuccess
                            ? intl.formatMessage(
                                  isGarage ? newsletterSmsThanksGarage : newsletterSmsThanksDynamite
                              )
                            : undefined
                    }
                    webHelperIcon={smsSubmittedWithSuccess ? ConfirmationIcon : undefined}
                    inputProps={({onChange}) => ({
                        'data-testid': 'sms-phone-input',
                        onChange: (e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
                            let formatted = digits
                            if (digits.length > 6) {
                                formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
                            } else if (digits.length > 3) {
                                formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`
                            }
                            e.target.value = formatted
                            onChange(e)
                            if (smsSubmittedWithSuccess) setSmsSubmittedWithSuccess(false)
                        }
                    })}
                />
                <Button
                    type="submit"
                    variant="webPrimary"
                    isLoading={isSmsSubmitting}
                    {...styles.smsButton}
                    data-testid="sms-submit-button"
                >
                    <FormattedMessage
                        defaultMessage="Sign up"
                        id="newsletter_sms_modal.cta"
                    />
                </Button>
            </form>
            {cmsSmsPrivacyText ? (
                <Box
                    sx={{
                        '& p': {textStyle: 'bodyXsRegular'},
                        '& a': {textDecoration: 'underline', color: 'webTypo.textSecondary'},
                        '& a:hover': {textDecoration: 'underline'}
                    }}
                    {...cmsSmsPrivacyStyles}
                    {...styles.smsDisclaimer}
                    dangerouslySetInnerHTML={{__html: cmsSmsPrivacyText}}
                />
            ) : (
                <Text textStyle="bodyXsRegular" {...styles.smsDisclaimer}>
                    {isGarage ? (
                    <FormattedMessage
                        defaultMessage="By sharing your phone number, you agree to receive automated marketing SMS from {brandName}. Not a condition of purchase. Msg & data rate may apply. <policy>[Privacy Policy]</policy>  <terms>[Terms and Conditions]</terms>"
                        id="newsletter_sms_modal.disclaimer.garage"
                        values={{
                            brandName,
                            policy: (chunks) => (
                                <ChakraLink
                                    as={Link}
                                    textStyle="bodyXsLink"
                                    color="webTypo.textSecondary"
                                    textDecoration="underline"
                                    to={privacyPolicyPath}
                                    data-testid="sms-privacy-policy-link"
                                >
                                    {chunks}
                                </ChakraLink>
                            ),
                            terms: (chunks) => (
                                <ChakraLink
                                    as={Link}
                                    textStyle="bodyXsLink"
                                    color="webTypo.textSecondary"
                                    textDecoration="underline"
                                    to={termsAndConditionsPath}
                                    data-testid="sms-terms-link"
                                >
                                    {chunks}
                                </ChakraLink>
                            )
                        }}
                    />
                    ) : (
                    <FormattedMessage
                        defaultMessage="By sharing your phone number, you agree to receive automated marketing SMS from {brandName}. Not a condition of purchase. Msg & data rate may apply. <policy>[Privacy Policy]</policy>  <terms>[Terms and Conditions]</terms>"
                        id="newsletter_sms_modal.disclaimer.dynamite"
                        values={{
                            brandName,
                            policy: (chunks) => (
                                <ChakraLink
                                    as={Link}
                                    textStyle="bodyXsLink"
                                    color="webTypo.textSecondary"
                                    textDecoration="underline"
                                    to={privacyPolicyPath}
                                    data-testid="sms-privacy-policy-link"
                                >
                                    {chunks}
                                </ChakraLink>
                            ),
                            terms: (chunks) => (
                                <ChakraLink
                                    as={Link}
                                    textStyle="bodyXsLink"
                                    color="webTypo.textSecondary"
                                    textDecoration="underline"
                                    to={termsAndConditionsPath}
                                    data-testid="sms-terms-link"
                                >
                                    {chunks}
                                </ChakraLink>
                            )
                        }}
                    />
                    )}
                </Text>
            )}
        </>
    )

    const submitAriaLabel = intl.formatMessage({
        defaultMessage: 'Submit',
        id: 'newsletter_cta.label'
    })

    return (
        <Box {...otherProps}>
            {cmsTitleText ? (
                <Box
                    sx={{
                        '& p': {textStyle: 'bodySmRegular'},
                        '& h1': {textStyle: 'webH1'},
                        '& h2': {textStyle: 'webH2'},
                        '& h3': {textStyle: 'webH3'},
                        '& h4, & h5, & h6': {textStyle: 'webH4'},
                        '& a': {textDecoration: 'underline'},
                        '& a:hover': {textDecoration: 'underline'}
                    }}
                    {...cmsTitleStyles}
                    dangerouslySetInnerHTML={{__html: cmsTitleText}}
                />
            ) : (
                <>
                    <Text {...styles.title} {...styles.largeTypo}>
                        {isGarage ? (
                            <FormattedMessage
                                defaultMessage="Stay in the know"
                                id="footer.subscribe.heading.stay_in_know.garage"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Stay in the know"
                                id="footer.subscribe.heading.stay_in_know.dynamite"
                            />
                        )}
                    </Text>

                </>
            )}
            <>
                <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                    <Box {...styles.fieldContainer}>
                        <Field
                            key="email"
                            name="newsletterEmail"
                            type="email"
                            variant="webFloatingField"
                            control={control}
                            defaultValue=""
                            label={
                                isGarage
                                    ? intl.formatMessage({
                                          defaultMessage: 'Email Address',
                                          id: 'newsletter_email.label.garage'
                                      })
                                    : intl.formatMessage({
                                          defaultMessage: 'Email Address',
                                          id: 'newsletter_email.label.dynamite'
                                      })
                            }
                            rules={{
                                required: isGarage
                                    ? intl.formatMessage(newsletterEmailInvalidGarage)
                                    : intl.formatMessage(newsletterEmailInvalidDynamite),
                                pattern: {
                                    value: /^[A-Za-z0-9]+(?:[._%+-][A-Za-z0-9]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.[A-Za-z]{2,}$/,
                                    message: isGarage
                                        ? intl.formatMessage({
                                              defaultMessage:
                                                  'Enter a valid Email address in the format name@example.com',
                                              id: 'newsletter.field.error.pattern_email.garage'
                                          })
                                        : intl.formatMessage({
                                              defaultMessage:
                                                  'Enter a valid Email address in the format name@example.com',
                                              id: 'newsletter.field.error.pattern_email.dynamite'
                                          })
                                }
                            }}
                            error={errors?.newsletterEmail}
                            formControlProps={{isDisabled: isSubmitting}}
                            webHelperText={
                                submittedWithSuccess
                                    ? isGarage
                                        ? intl.formatMessage({
                                              defaultMessage: 'Thanks for subscribing',
                                              id: 'newsletter_confirmation.message.garage'
                                          })
                                        : intl.formatMessage({
                                              defaultMessage: 'Thanks for subscribing',
                                              id: 'newsletter_confirmation.message.dynamite'
                                          })
                                    : undefined
                            }
                            webHelperIcon={submittedWithSuccess ? ConfirmationIcon : undefined}
                            inputProps={({onChange}) => ({
                                'data-testid': 'email-input',
                                paddingRight: '56px',
                                onChange: (e) => {
                                    onChange(e)
                                    form.clearErrors('formError')
                                    if (submittedWithSuccess) setSubmittedWithSuccess(false)
                                    if (!e.target.value) {
                                        form.clearErrors('newsletterEmail')
                                    }
                                }
                            })}
                        />
                        <IconButton
                            type="submit"
                            title={submitAriaLabel}
                            aria-label={submitAriaLabel}
                            data-testid="newsletter-submit-button"
                            icon={
                                isSubmitting ? (
                                    <Spinner mt={'5px'} size="sm" color="white" />
                                ) : (
                                    <RightArrowWhite boxSize="24px" />
                                )
                            }
                            pointerEvents={isSubmitting ? 'none' : 'auto'}
                            width="48px"
                            height="48px"
                            minW="48px"
                            borderRadius={0}
                            bg="webButton.btnPrimaryDefault"
                            color="webTypo.textPrimaryReverse"
                            _hover={{bg: 'webButton.btnPrimaryHover'}}
                            _active={{bg: 'webButton.btnPrimaryHover'}}
                            {...styles.submitButton}
                            top="0"
                        />

                        {errors.formError && (
                            <Text {...styles.fieldError}>{errors.formError.message}</Text>
                        )}
                    </Box>
                </form>

                {cmsPrivacyText ? (
                    <Box
                        {...styles.disclaimerText}
                        sx={{
                            '& p': {textStyle: 'bodySmRegular'},
                            '& a': {textDecoration: 'underline'},
                            '& a:hover': {textDecoration: 'underline'}
                        }}
                        {...cmsPrivacyStyles}
                        dangerouslySetInnerHTML={{__html: cmsPrivacyText}}
                    />
                ) : (
                    <Text {...styles.disclaimerText}>
                        <FormattedMessage
                            id="newsletter_disclaimer.text"
                            defaultMessage="By signing up for SMS, you agree to receive promotional text messages from {brandName}. Message and data rates may apply. Message frequency varies. Reply STOP to unsubscribe. See <policy>Privacy Policy</policy> and <terms>Terms</terms>."
                            values={{
                                brandName,
                                policy: (chunks) => (
                                    <ChakraLink
                                        as={Link}
                                        textDecoration="underline"
                                        to={privacyPolicyPath}
                                        data-testid="privacy-policy-link"
                                    >
                                        {chunks}
                                    </ChakraLink>
                                ),
                                terms: (chunks) => (
                                    <ChakraLink
                                        as={Link}
                                        textDecoration="underline"
                                        to={termsAndConditionsPath}
                                        data-testid="terms-link"
                                    >
                                        {chunks}
                                    </ChakraLink>
                                )
                            }}
                        />
                    </Text>
                )}
            </>

            <WebDrawer
                isOpen={!isDesktop && isSmsModalOpen}
                onClose={closeSmsModal}
                hideHeader
                size="min"
                noBodyStyle
                contentSx={{borderRadius: 0}}
            >
                <Box {...styles.smsContentBox}>{smsFormContent}</Box>
            </WebDrawer>

            <Modal
                isOpen={!!isDesktop && isSmsModalOpen}
                onClose={closeSmsModal}
                isCentered
            >
                <ModalOverlay />
                <ModalContent {...styles.smsModalContent}>
                    <ModalBody {...styles.smsModalBody}>
                        <Flex>
                            <Box {...styles.smsImageBox}>
                                <Img
                                    src={smsImageUrl || getAssetUrl('static/img/sms-modal-fallback.png')}
                                    alt=""
                                    {...styles.smsImage}
                                />
                            </Box>
                            <Box {...styles.smsContentBox}>{smsFormContent}</Box>
                        </Flex>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    )
}

NewsletterSignUp.propTypes = {
    otherProps: PropTypes.object
}

export default NewsletterSignUp
